import { ComprehensiveScenario, ScenarioAuditEntry, ScenarioCollection } from '../types/scenarios';
import { getForecastTerm } from '@/lib/terminology-config';
import { applyScenarioToForecast } from '../lib/scenarioEngine';
import { generateForecastData } from './demo-data';
import { format } from 'date-fns';

// Reference date: Always current date
const NOW = new Date();
NOW.setHours(10, 0, 0, 0); // Set to 10am for consistency

// Helper to create dates relative to NOW
const daysFromNow = (days: number) => new Date(NOW.getTime() + days * 24 * 60 * 60 * 1000);
const daysAgo = (days: number) => daysFromNow(-days);
const hoursFromNow = (hours: number) => new Date(NOW.getTime() + hours * 60 * 60 * 1000);

// Helper to create a scenario audit entry
let _auditSeq = 0;
const makeScenarioAudit = (
  scenarioId: string,
  actorId: string,
  actorName: string,
  eventType: ScenarioAuditEntry['eventType'],
  timestamp: Date,
  fields?: Partial<Omit<ScenarioAuditEntry, 'id' | 'scenarioId' | 'timestamp' | 'actorId' | 'actorName' | 'eventType'>>,
): ScenarioAuditEntry => ({
  id: `sa-${scenarioId}-${++_auditSeq}`,
  scenarioId,
  timestamp,
  actorId,
  actorName,
  eventType,
  ...fields,
});

export const COMPREHENSIVE_SCENARIOS: ComprehensiveScenario[] = [
  // 1. Cost Cutting Plan - Draft
  {
    id: 'scenario-cost-cut',
    name: 'Cost Cutting Plan',
    description: 'Emergency cost reduction: 10% across all categories to preserve cash runway during economic uncertainty. Focus on discretionary spending while maintaining core operations.',
    status: 'draft',
    priority: 'none',
    label: null,
    category: 'cost-cutting',
    createdBy: '4',
    createdByName: 'Linda Kowalski',
    createdAt: daysFromNow(-3),
    modifiedAt: daysFromNow(-3),
    modifiedBy: 'Linda Kowalski',
    shares: [
      {
        id: 'share-1',
        userId: '6',
        userName: 'Sarah Mitchell',
        permission: 'edit',
        sharedAt: new Date('2026-02-05T10:00:00'),
        sharedBy: '4',
      },
      {
        id: 'share-2',
        userId: '5',
        userName: 'Marcus Patel',
        permission: 'comment',
        sharedAt: new Date('2026-02-05T10:00:00'),
        sharedBy: '4',
      },
      {
        id: 'share-3',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: new Date('2026-02-06T09:00:00'),
        sharedBy: '4',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-cost-1',
        type: 'change-costs',
        name: 'Reduce Materials Spend',
        description: 'Negotiate better rates with suppliers - 10% reduction',
        amount: 13500,
        date: daysFromNow(2), // Feb 2026
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92), // Apr 2026
        details: {
          category: 'materials',
          changeType: 'reduction',
          period: 'Feb-Apr 2026',
          monthlySavings: 4500,
          totalSavings: 13500,
        },
        createdAt: daysFromNow(-3),
        createdBy: 'Linda Kowalski',
      },
      {
        id: 'change-cost-2',
        type: 'change-costs',
        name: 'Reduce Subcontractor Costs',
        description: 'Renegotiate contracts - 10% reduction',
        amount: 11400,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          category: 'subcontractors',
          changeType: 'reduction',
          period: 'Feb-Apr 2026',
          monthlySavings: 3800,
          totalSavings: 11400,
        },
        createdAt: daysFromNow(-3),
        createdBy: 'Linda Kowalski',
      },
      {
        id: 'change-cost-3',
        type: 'delay-payment',
        name: 'Delay Equipment Purchase',
        description: 'Move £25K equipment purchase from Feb 7 to May 7',
        amount: 25000,
        supplier: 'Equipment Purchase',
        originalDueDate: daysFromNow(9), // Feb 7
        newDueDate: daysFromNow(99), // May 7
        delayDays: 90,
        date: daysFromNow(9),
        effectiveDate: daysFromNow(99),
        details: {
          item: 'Equipment',
        },
        createdAt: daysFromNow(-3),
        createdBy: 'Linda Kowalski',
      },
      {
        id: 'change-cost-4',
        type: 'change-costs',
        name: 'Reduce Marketing Spend',
        description: 'Cut marketing budget by 20%',
        amount: 4800,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          category: 'marketing',
          changeType: 'reduction',
          period: 'Feb-Apr 2026',
          monthlySavings: 1600,
          totalSavings: 4800,
        },
        createdAt: daysFromNow(-3),
        createdBy: 'Linda Kowalski',
      },
    ],
    dependencies: [
      {
        id: 'dep-cost-cut-hire',
        type: 'conflict',
        targetScenarioId: 'scenario-hire',
        impact: 'Cannot implement cost reductions while expanding engineering team - conflicting resource allocation',
        isMet: false,
      },
    ],
    comments: [
      {
        id: 'comment-1',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Sarah, can you review these cost reduction targets and confirm they\'re achievable without impacting our key supplier relationships?',
        createdAt: new Date('2026-02-05T14:30:00'),
      },
      {
        id: 'comment-2',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Reviewed - I\'ve excluded our top 3 suppliers (Jewson, Travis Perkins, ABC Subcontractors) from the 10% cut. The revised impact is still £52K positive.',
        createdAt: new Date('2026-02-05T16:45:00'),
      },
      {
        id: 'comment-3',
        userId: '5',
        authorName: 'Marcus Patel',
        userRole: 'Regional Finance Director',
        text: 'This aligns with our regional cost targets. Manchester and Leeds sites can implement these cuts immediately.',
        createdAt: new Date('2026-02-06T09:15:00'),
      },
    ],
    commentCount: 3,
    versions: [
      {
        id: 'v1-cost',
        version: 1,
        description: 'Initial draft',
        createdBy: 'Linda Kowalski',
        createdByName: 'Linda Kowalski',
        createdAt: daysFromNow(-3),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 135000,
      minCashDate: daysFromNow(63),
      runway: 20,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: -10,
      costChangeAmount: -54700,
      netImpact: 54700,
      endingCash: 301700,
    },
    tags: ['Cost Control', 'Efficiency'],
    impactHistory: [
      { id: 'ih-cc-1', dateTime: new Date(NOW.getTime() - 3 * 24 * 60 * 60 * 1000 - 4 * 60 * 60 * 1000), refreshType: 'Scheduled', triggeredBy: 'System', previousValue: 51200, newValue: 53100 },
      { id: 'ih-cc-2', dateTime: new Date(NOW.getTime() - 2 * 24 * 60 * 60 * 1000), refreshType: 'Scheduled', triggeredBy: 'System', previousValue: 53100, newValue: 54700 },
      { id: 'ih-cc-3', dateTime: new Date(NOW.getTime() - 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000), refreshType: 'Manual', triggeredBy: 'Sarah Mitchell', previousValue: 54700, newValue: 54700 },
    ],
    auditLog: [
      makeScenarioAudit('scenario-cost-cut', '4', 'Linda Kowalski', 'created', daysAgo(3), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-cost-cut', '4', 'Linda Kowalski', 'shared', daysAgo(3), { detail: 'Shared with Sarah Mitchell (edit)' }),
      makeScenarioAudit('scenario-cost-cut', '4', 'Linda Kowalski', 'shared', daysAgo(3), { detail: 'Shared with Marcus Patel (comment)' }),
      makeScenarioAudit('scenario-cost-cut', '6', 'Sarah Mitchell', 'edited', daysAgo(2), { detail: 'Revised cost reduction targets — excluded top 3 suppliers' }),
      makeScenarioAudit('scenario-cost-cut', '4', 'Linda Kowalski', 'shared', daysAgo(2), { detail: 'Shared with Catherine Hughes (view)' }),
    ],
  },

  // 2. Win BigCo Contract - Draft
  {
    id: 'scenario-bigco',
    name: 'Win BigCo Contract',
    description: 'Major new contract with BigCo Ltd - £500K value, 60-day payment terms. First invoice Feb 15, payment expected Apr 16.',
    status: 'under-review',
    priority: 'high',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-4),
    modifiedAt: daysFromNow(-2),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-bigco-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'edit',
        sharedAt: daysFromNow(-4),
        sharedBy: '6',
      },
      {
        id: 'share-bigco-2',
        userId: '3',
        userName: 'Edna Foster',
        permission: 'comment',
        sharedAt: daysFromNow(-3),
        sharedBy: '6',
      },
      {
        id: 'share-bigco-3',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-2),
        sharedBy: '4',
      },
    ],
    isFavorite: true,
    changes: [
      {
        id: 'change-bigco-1',
        type: 'win-customer',
        name: 'First Milestone Invoice',
        description: 'BigCo Ltd - First milestone payment',
        amount: 125000,
        date: daysFromNow(77), // Apr 16, 2026 (60 days from Feb 15)
        effectiveDate: daysFromNow(77),
        details: {
          customer: 'BigCo Ltd',
          invoiceDate: daysFromNow(17), // Feb 15
          paymentTerms: 60,
          milestone: 'First Milestone',
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-bigco-2',
        type: 'win-customer',
        name: 'Second Milestone Invoice',
        description: 'BigCo Ltd - Second milestone payment',
        amount: 125000,
        date: daysFromNow(106), // May 15, 2026 (60 days from Mar 15)
        effectiveDate: daysFromNow(106),
        details: {
          customer: 'BigCo Ltd',
          invoiceDate: daysFromNow(46), // Mar 15
          paymentTerms: 60,
          milestone: 'Second Milestone',
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-bigco-3',
        type: 'change-costs',
        name: 'Additional Materials',
        description: 'Materials for BigCo project',
        amount: -120000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          category: 'materials',
          period: 'Feb-Apr 2026',
          monthlyCost: 40000,
          totalCost: 120000,
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-bigco-4',
        type: 'change-costs',
        name: 'Additional Subcontractors',
        description: 'Subcontractor costs for BigCo project',
        amount: -90000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          category: 'subcontractors',
          period: 'Feb-Apr 2026',
          monthlyCost: 30000,
          totalCost: 90000,
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    comments: [
      {
        id: 'comment-bigco-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'I\'ve modelled this on 60-day payment terms as agreed with BigCo\'s procurement team. The upfront materials and subcontractor costs create a tight window in March — we\'ll need to ensure the overdraft facility is available if needed.',
        createdAt: hoursFromNow(-90),
      },
      {
        id: 'comment-bigco-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'The March cash dip concerns me — we\'d be at £125K which is right on the healthy threshold. Can you check if BigCo would accept a 30-day first milestone to improve the timing? Even just the first invoice on shorter terms would help significantly.',
        createdAt: hoursFromNow(-72),
      },
      {
        id: 'comment-bigco-3',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Spoke to their procurement lead — they\'re open to 45 days on the first milestone, standard 60 thereafter. I\'ve updated the model. That brings the March low point up to around £148K. Much more comfortable.',
        createdAt: hoursFromNow(-48),
      },
      {
        id: 'comment-bigco-4',
        userId: '3',
        authorName: 'Edna Foster',
        userRole: 'Accounts Assistant',
        text: 'Just flagging — BigCo\'s standard T&Cs include a 5% retention clause on all invoices until practical completion. That\'s not reflected here. Could affect the final milestone amount by ~£25K.',
        createdAt: hoursFromNow(-24),
      },
      {
        id: 'comment-bigco-5',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Good spot Edna — I\'ll add the retention adjustment to v3 once we confirm the contract terms. Linda, are you happy to approve in principle on the current model while we finalise the retention position?',
        createdAt: hoursFromNow(-12),
      },
    ],
    commentCount: 5,
    versions: [
      {
        id: 'v1-bigco',
        version: 1,
        description: 'Initial draft — 60-day payment terms, full subcontractor cost model',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-4),
        data: {
          changes: [
            {
              id: 'change-bigco-1-v1',
              type: 'win-customer',
              name: 'First Milestone Invoice',
              description: 'BigCo Ltd - First milestone payment (original 60-day terms)',
              amount: 125000,
              date: daysFromNow(77),
              effectiveDate: daysFromNow(77),
              details: {
                customer: 'BigCo Ltd',
                invoiceDate: daysFromNow(17),
                paymentTerms: 60,
                milestone: 'First Milestone',
              },
              createdAt: daysFromNow(-4),
              createdBy: 'Sarah Mitchell',
            },
            {
              id: 'change-bigco-3-v1',
              type: 'change-costs',
              name: 'Additional Materials',
              description: 'Materials for BigCo project',
              amount: -120000,
              date: daysFromNow(2),
              effectiveDate: daysFromNow(2),
              endDate: daysFromNow(92),
              details: {
                category: 'materials',
                period: 'Feb-Apr 2026',
                monthlyCost: 40000,
                totalCost: 120000,
              },
              createdAt: daysFromNow(-4),
              createdBy: 'Sarah Mitchell',
            },
          ],
        },
      },
      {
        id: 'v2-bigco',
        version: 2,
        description: 'Updated first milestone to 45-day terms following BigCo discussion — March cash low point improved by £23K',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-2),
        data: {
          changes: [
            {
              id: 'change-bigco-1-v2',
              type: 'win-customer',
              name: 'First Milestone Invoice',
              description: 'BigCo Ltd - First milestone payment (revised to 45-day terms)',
              amount: 125000,
              date: daysFromNow(62),
              effectiveDate: daysFromNow(62),
              details: {
                customer: 'BigCo Ltd',
                invoiceDate: daysFromNow(17),
                paymentTerms: 45,
                milestone: 'First Milestone',
              },
              createdAt: daysFromNow(-2),
              createdBy: 'Sarah Mitchell',
            },
            {
              id: 'change-bigco-2-v2',
              type: 'win-customer',
              name: 'Second Milestone Invoice',
              description: 'BigCo Ltd - Second milestone payment',
              amount: 125000,
              date: daysFromNow(106),
              effectiveDate: daysFromNow(106),
              details: {
                customer: 'BigCo Ltd',
                invoiceDate: daysFromNow(46),
                paymentTerms: 60,
                milestone: 'Second Milestone',
              },
              createdAt: daysFromNow(-2),
              createdBy: 'Sarah Mitchell',
            },
            {
              id: 'change-bigco-3-v2',
              type: 'change-costs',
              name: 'Additional Materials',
              description: 'Materials for BigCo project',
              amount: -120000,
              date: daysFromNow(2),
              effectiveDate: daysFromNow(2),
              endDate: daysFromNow(92),
              details: {
                category: 'materials',
                period: 'Feb-Apr 2026',
                monthlyCost: 40000,
                totalCost: 120000,
              },
              createdAt: daysFromNow(-4),
              createdBy: 'Sarah Mitchell',
            },
          ],
        },
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 125000,
      minCashDate: daysFromNow(63),
      runway: 24,
      revenueChange: 20,
      revenueChangeAmount: 250000,
      costChange: 15,
      costChangeAmount: 210000,
      netImpact: 40000,
      endingCash: 287000,
    },
    tags: ['Growth', 'New Business'],
    impactHistory: [
      { id: 'ih-bc-1', dateTime: new Date(NOW.getTime() - 5 * 24 * 60 * 60 * 1000), refreshType: 'Scheduled', triggeredBy: 'System', previousValue: 37500, newValue: 38800 },
      { id: 'ih-bc-2', dateTime: new Date(NOW.getTime() - 4 * 24 * 60 * 60 * 1000), refreshType: 'Scheduled', triggeredBy: 'System', previousValue: 38800, newValue: 40000 },
    ],
    auditLog: [
      makeScenarioAudit('scenario-bigco', '6', 'Sarah Mitchell', 'created', daysAgo(4), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-bigco', '6', 'Sarah Mitchell', 'shared', daysAgo(4), { detail: 'Shared with Linda Kowalski (edit)' }),
      makeScenarioAudit('scenario-bigco', '6', 'Sarah Mitchell', 'edited', daysAgo(3), { detail: 'Shared with Edna Foster (comment)' }),
      makeScenarioAudit('scenario-bigco', '6', 'Sarah Mitchell', 'edited', daysAgo(2), { detail: 'Updated first milestone to 45-day terms — March low point improved by £23K' }),
      makeScenarioAudit('scenario-bigco', '4', 'Linda Kowalski', 'shared', daysAgo(2), { detail: 'Shared with Catherine Hughes (view)' }),
      makeScenarioAudit('scenario-bigco', '6', 'Sarah Mitchell', 'status-changed', daysAgo(1), { fromStatus: 'draft', toStatus: 'under-review', detail: 'Sent for peer review ahead of formal submission' }),
    ],
    issues: [
      {
        id: 'issue-bigco-1',
        type: 'transaction-completed' as const,
        severity: 'critical' as const,
        title: 'First Milestone Invoice has been receipted',
        description: 'The invoice "First Milestone Invoice" (BigCo Ltd — £125,000) has been marked as received in Access Financials since this adjustment was created. This adjustment may now be duplicating a transaction already in the base forecast.',
        detectedAt: daysAgo(1),
        relatedChangeId: 'change-bigco-1',
        relatedChangeName: 'First Milestone Invoice',
        impactAmount: 125000,
        impactDescription: '+£125,000 potentially double-counted in cash position',
      },
      {
        id: 'issue-bigco-2',
        type: 'base-forecast-changed' as const,
        severity: 'warning' as const,
        title: 'Base forecast has shifted since last review',
        description: `The base forecast has changed by £43,000 since this scenario was last reviewed ${format(daysAgo(1), 'd MMM yyyy')}. A scheduled forecast refresh updated the Access Financials data, affecting the net cash position underlying this scenario's assumptions.`,
        detectedAt: hoursFromNow(-6),
        impactAmount: -43000,
        impactDescription: '-£43,000 shift in base forecast since last review',
      },
    ],
  },

  // 4. Hire 5 Engineers - Draft
  {
    id: 'scenario-hire',
    name: 'Hire 5 Engineers',
    description: 'Expand engineering team to support new projects and growth. 5 new engineers at £55K average salary = £275K annual cost.',
    status: 'draft',
    priority: 'medium',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-5),
    modifiedAt: daysFromNow(-3),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-hire-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'comment',
        sharedAt: daysFromNow(-5),
        sharedBy: '6',
      },
      {
        id: 'share-hire-2',
        userId: '3',
        userName: 'Edna Foster',
        permission: 'view',
        sharedAt: daysFromNow(-4),
        sharedBy: '6',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-hire-1',
        type: 'change-costs',
        name: 'Recruitment Costs - February',
        description: 'Recruitment fees for 2 engineers',
        amount: -10000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        details: {
          category: 'recruitment',
          engineers: 2,
          feePerEngineer: 5000,
        },
        createdAt: daysFromNow(-5),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-2',
        type: 'change-costs',
        name: 'Recruitment Costs - March',
        description: 'Recruitment fees for 2 engineers',
        amount: -10000,
        date: daysFromNow(31),
        effectiveDate: daysFromNow(31),
        details: {
          category: 'recruitment',
          engineers: 2,
          feePerEngineer: 5000,
        },
        createdAt: daysFromNow(-5),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-3',
        type: 'change-costs',
        name: 'Recruitment Costs - April',
        description: 'Recruitment fees for 1 engineer',
        amount: -5000,
        date: daysFromNow(62),
        effectiveDate: daysFromNow(62),
        details: {
          category: 'recruitment',
          engineers: 1,
          feePerEngineer: 5000,
        },
        createdAt: daysFromNow(-5),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-4',
        type: 'hire-employees',
        name: 'Payroll Increase - February',
        description: '2 engineers, partial month',
        amount: -9000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        details: {
          count: 2,
          department: 'Engineering',
          partialMonth: true,
          monthlyCost: 9000,
        },
        createdAt: daysFromNow(-5),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-5',
        type: 'hire-employees',
        name: 'Payroll Increase - March',
        description: '4 engineers (2 full month + 2 partial)',
        amount: -18000,
        date: daysFromNow(31),
        effectiveDate: daysFromNow(31),
        details: {
          count: 4,
          department: 'Engineering',
          monthlyCost: 18000,
        },
        createdAt: daysFromNow(-5),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-6',
        type: 'hire-employees',
        name: 'Payroll Increase - April',
        description: '5 engineers, full month',
        amount: -23000,
        date: daysFromNow(62),
        effectiveDate: daysFromNow(62),
        details: {
          count: 5,
          department: 'Engineering',
          monthlyCost: 23000,
        },
        createdAt: daysFromNow(-5),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    comments: [
      {
        id: 'comment-hire-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'This model assumes staggered starts: 2 engineers in February, 2 in March, 1 in April. Recruitment fees are based on 10% of first year salary. The April cash low point of £62K is a concern — we\'d be below our healthy threshold.',
        createdAt: hoursFromNow(-80),
      },
      {
        id: 'comment-hire-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'I\'m not comfortable with £62K — that\'s below our caution threshold and leaves us very exposed. Is this contingent on the BigCo contract coming through? Because if it is, we should sequence these decisions rather than model them independently.',
        createdAt: hoursFromNow(-60),
      },
      {
        id: 'comment-hire-3',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'You\'re right — I\'ve kept these as separate scenarios deliberately so we can see the individual impact. In practice, if BigCo lands we\'d have £148K+ at the low point even with the hiring. I can put together a combined view if that helps the decision.',
        createdAt: hoursFromNow(-36),
      },
      {
        id: 'comment-hire-4',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Yes please — a combined scenario would be useful. Also worth noting the conflict flag with Cost Cutting is correct. We can\'t run both simultaneously. This should be parked until we have clarity on BigCo.',
        createdAt: hoursFromNow(-20),
      },
    ],
    commentCount: 4,
    versions: [
      {
        id: 'v1-hire',
        version: 1,
        description: 'Initial creation — all 5 engineers starting February',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-5),
        data: {},
      },
      {
        id: 'v2-hire',
        version: 2,
        description: 'Revised to staggered starts (Feb/Mar/Apr) — reduces peak cash requirement in February by £14K',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-3),
        data: {},
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 62000,
      minCashDate: daysFromNow(75),
      runway: 12,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: 15,
      costChangeAmount: 75000,
      netImpact: -75000,
      endingCash: 172000,
    },
    tags: ['Growth', 'Hiring', 'Risk'],
    impactHistory: [
      { id: 'ih-hire-1', dateTime: new Date(NOW.getTime() - 2 * 24 * 60 * 60 * 1000), refreshType: 'Scheduled', triggeredBy: 'System', previousValue: -71000, newValue: -73500 },
      { id: 'ih-hire-2', dateTime: new Date(NOW.getTime() - 24 * 60 * 60 * 1000), refreshType: 'Manual', triggeredBy: 'Linda Kowalski', previousValue: -73500, newValue: -75000 },
      { id: 'ih-hire-3', dateTime: new Date(NOW.getTime() - 6 * 60 * 60 * 1000), refreshType: 'Scheduled', triggeredBy: 'System', previousValue: -75000, newValue: -75000 },
    ],
    auditLog: [
      makeScenarioAudit('scenario-hire', '6', 'Sarah Mitchell', 'created', daysAgo(5), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-hire', '6', 'Sarah Mitchell', 'shared', daysAgo(5), { detail: 'Shared with Linda Kowalski (comment)' }),
      makeScenarioAudit('scenario-hire', '6', 'Sarah Mitchell', 'edited', daysAgo(3), { detail: 'Revised to staggered starts (Feb/Mar/Apr) — reduces peak cash requirement in February by £14K' }),
      makeScenarioAudit('scenario-hire', '6', 'Sarah Mitchell', 'shared', daysAgo(4), { detail: 'Shared with Edna Foster (view)' }),
    ],
  },

  // 5. A: Conservative Growth - Active
  {
    id: 'scenario-conservative',
    name: 'A: Conservative Growth',
    description: 'Cautious approach: minimal new hires, focus on existing clients, conservative revenue assumptions. Prioritise cash preservation over growth.',
    status: 'active',
    priority: 'none',
    label: 'A',
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-7),
    modifiedAt: daysFromNow(-7),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Sarah Mitchell',
      },
      {
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-cons-1',
        type: 'change-costs',
        name: 'Revenue Reduction - Cautious Forecast',
        description: 'Assume 5% revenue reduction due to economic uncertainty',
        amount: -22500,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          category: 'revenue',
          period: 'Feb-Apr 2026',
          percentageReduction: 5,
          totalReduction: 22500,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-cons-2',
        type: 'change-costs',
        name: 'Improved Collections',
        description: 'Reduce DSO from 35 to 30 days through active chasing',
        amount: 12500,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          category: 'collections',
          period: 'Feb-Apr 2026',
          dsoReduction: 5,
          cashTimingBenefit: 12500,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-cons-3',
        type: 'delay-payment',
        name: 'Delay Equipment Purchase',
        description: 'Delay £15K equipment by 1 month',
        amount: 15000,
        supplier: 'Equipment Purchase',
        originalDueDate: daysFromNow(17), // Feb 15
        newDueDate: daysFromNow(46), // Mar 15
        delayDays: 29,
        date: daysFromNow(17),
        effectiveDate: daysFromNow(46),
        details: {
          item: 'Equipment',
          timingBenefit: 5000,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-cons-1',
        userId: '6',
        approverId: '6',
        userName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        status: 'approved',
        timestamp: daysFromNow(-7),
        order: 1,
      },
      {
        id: 'approval-cons-2',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Safe approach in current climate — approved',
        timestamp: daysFromNow(-7),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-cons-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'I\'ve built this as the downside case — assumes economic conditions deteriorate slightly in Q1. The DSO improvement from 35 to 30 days is achievable with the collections push Edna has already started on the Mersey Build Group account.',
        createdAt: daysFromNow(-7),
      },
      {
        id: 'comment-cons-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Good reference point to have. Even in this scenario we\'re staying above £95K which is comfortably above caution threshold. This is essentially our floor. Happy to approve as the conservative baseline — it gives the board confidence we\'ve stress-tested the downside.',
        createdAt: daysFromNow(-7),
      },
      {
        id: 'comment-cons-3',
        userId: '1',
        authorName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        text: 'Sensible. I\'d like to see this alongside B and C for the board pack so they can see the full range. Can you make sure all three are ready for next Tuesday?',
        createdAt: daysFromNow(-6),
      },
    ],
    commentCount: 3,
    versions: [
      {
        id: 'v1-cons',
        version: 1,
        description: 'Initial creation — 5% revenue reduction, collections improvement, equipment delay',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-7),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 95000,
      minCashDate: daysFromNow(70),
      runway: 16,
      revenueChange: -5,
      revenueChangeAmount: -22500,
      costChange: 0,
      costChangeAmount: 0,
      netImpact: -5000,
      endingCash: 242000,
    },
    tags: ['Cautious', 'Risk Management'],
    auditLog: [
      makeScenarioAudit('scenario-conservative', '6', 'Sarah Mitchell', 'created', daysAgo(7), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-conservative', '6', 'Sarah Mitchell', 'shared', daysAgo(7), { detail: 'Shared with Linda Kowalski (view)' }),
      makeScenarioAudit('scenario-conservative', '6', 'Sarah Mitchell', 'shared', daysAgo(7), { detail: 'Shared with Catherine Hughes (view)' }),
      makeScenarioAudit('scenario-conservative', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(7), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-conservative', '4', 'Linda Kowalski', 'approval-granted', daysAgo(7), { fromStatus: 'pending-approval', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review', detail: 'Safe approach in current climate — advancing to CEO Sign-off' }),
      makeScenarioAudit('scenario-conservative', '1', 'Catherine Hughes', 'approval-granted', daysAgo(7), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 2, stageName: 'CEO Sign-off', detail: 'Good reference point for the board pack — approved' }),
    ],
  },

  // 6. B: Moderate Growth - Draft
  {
    id: 'scenario-moderate',
    name: 'B: Moderate Growth',
    description: 'Balanced approach: strategic hiring + new business development. Measured growth with acceptable risk.',
    status: 'draft',
    priority: 'none',
    label: 'B',
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-7),
    modifiedAt: daysFromNow(-7),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Sarah Mitchell',
      },
      {
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-mod-1',
        type: 'hire-employees',
        name: 'Hire 2 Engineers',
        description: 'Strategic hires starting Mar 1',
        amount: -18000,
        date: daysFromNow(31), // Mar 1
        effectiveDate: daysFromNow(31),
        endDate: daysFromNow(92),
        details: {
          count: 2,
          department: 'Engineering',
          period: 'Mar-Apr 2026',
          monthlyCost: 9000,
          totalCost: 18000,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-mod-2',
        type: 'change-costs',
        name: 'Marketing Investment - February',
        description: 'Sales and marketing campaign',
        amount: -10000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        details: {
          category: 'marketing',
          campaign: 'Q1 Sales Push',
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-mod-3',
        type: 'change-costs',
        name: 'Marketing Investment - March',
        description: 'Sales and marketing campaign',
        amount: -10000,
        date: daysFromNow(31),
        effectiveDate: daysFromNow(31),
        details: {
          category: 'marketing',
          campaign: 'Q1 Sales Push',
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-mod-4',
        type: 'win-customer',
        name: 'Revenue Growth',
        description: '10% growth from new business (Mar-Apr)',
        amount: 30000,
        date: daysFromNow(31),
        effectiveDate: daysFromNow(31),
        endDate: daysFromNow(92),
        details: {
          period: 'Mar-Apr 2026',
          percentageGrowth: 10,
          monthlyIncrease: 15000,
          totalIncrease: 30000,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    comments: [
      {
        id: 'comment-mod-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'This is the middle path — 2 strategic hires in March and a Q1 marketing push to drive 10% revenue growth from April. Cash stays above £105K throughout which keeps us in the healthy zone. I think this is the most realistic outcome.',
        createdAt: daysFromNow(-7),
      },
      {
        id: 'comment-mod-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'The revenue growth assumption of 10% from the marketing campaign — what\'s that based on? I want to make sure we\'re not being too optimistic here. The £10K/month marketing spend needs to have a credible conversion assumption behind it.',
        createdAt: daysFromNow(-6),
      },
      {
        id: 'comment-mod-3',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Based on last year\'s Q1 campaign which generated 8% uplift on a lower spend. I\'ve been conservative at 10% rather than extrapolating linearly. Happy to sensitise it down to 7% if you\'d prefer — that still shows a positive net impact.',
        createdAt: daysFromNow(-6),
      },
      {
        id: 'comment-mod-4',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'That context helps — 10% is defensible then. I\'d suggest we add a sensitivity note to the board pack showing the 7% scenario. Can you add that as a version so we have it documented?',
        createdAt: daysFromNow(-5),
      },
      {
        id: 'comment-mod-5',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Done — v2 has the 7% sensitivity. Net impact drops from -£18K to -£27K but we\'re still cash positive above £100K. Either version is boardable.',
        createdAt: daysFromNow(-4),
      },
    ],
    commentCount: 5,
    versions: [
      {
        id: 'v1-mod',
        version: 1,
        description: 'Initial creation — 10% revenue growth assumption from Q1 marketing',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-7),
        data: {},
      },
      {
        id: 'v2-mod',
        version: 2,
        description: 'Added 7% revenue sensitivity per Linda\'s request — net impact -£27K, still cash positive above £100K',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-4),
        data: {},
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 105000,
      minCashDate: daysFromNow(68),
      runway: 18,
      revenueChange: 10,
      revenueChangeAmount: 30000,
      costChange: 10,
      costChangeAmount: 38000,
      netImpact: -18000,
      endingCash: 229000,
    },
    tags: ['Balanced', 'Strategic'],
    auditLog: [
      makeScenarioAudit('scenario-moderate', '6', 'Sarah Mitchell', 'created', daysAgo(7), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-moderate', '6', 'Sarah Mitchell', 'shared', daysAgo(7), { detail: 'Shared with Linda Kowalski (view)' }),
      makeScenarioAudit('scenario-moderate', '6', 'Sarah Mitchell', 'shared', daysAgo(7), { detail: 'Shared with Catherine Hughes (view)' }),
      makeScenarioAudit('scenario-moderate', '6', 'Sarah Mitchell', 'edited', daysAgo(4), { detail: 'Added 7% revenue sensitivity per Linda\'s request' }),
    ],
  },

  // 7. C: Aggressive Growth - Active
  {
    id: 'scenario-aggressive',
    name: 'C: Aggressive Growth',
    description: 'High-growth strategy: major hiring + multiple new contracts. Aggressive market expansion with calculated risk.',
    status: 'active',
    priority: 'none',
    label: 'C',
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-7),
    modifiedAt: daysFromNow(-7),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Sarah Mitchell',
      },
      {
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-agg-1',
        type: 'win-customer',
        name: 'New Contract 1',
        description: 'Major client contract milestone',
        amount: 125000,
        date: daysFromNow(46), // Mar 15, 2026
        effectiveDate: daysFromNow(46),
        details: {
          customer: 'Major Client',
          milestone: 'Contract 1',
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-agg-2',
        type: 'win-customer',
        name: 'New Contract 2',
        description: 'Medium client contract',
        amount: 100000,
        date: daysFromNow(63), // Apr 1, 2026
        effectiveDate: daysFromNow(63),
        details: {
          customer: 'Medium Client',
          milestone: 'Contract 2',
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-agg-3',
        type: 'win-customer',
        name: 'New Contract 3',
        description: 'Additional project win',
        amount: 80000,
        date: daysFromNow(77), // Apr 15, 2026
        effectiveDate: daysFromNow(77),
        details: {
          customer: 'Additional Client',
          milestone: 'Contract 3',
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-agg-4',
        type: 'hire-employees',
        name: 'Hire 8 Engineers + 2 Project Managers',
        description: 'Major team expansion (staggered starts Feb-Mar)',
        amount: -135000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(92),
        details: {
          count: 10,
          department: 'Engineering & PM',
          period: 'Feb-Apr 2026',
          rampingCost: 135000,
          aprilMonthlyCost: 45000,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-agg-5',
        type: 'change-costs',
        name: 'Recruitment Costs',
        description: 'Hiring fees for 10 positions',
        amount: -50000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(31),
        details: {
          category: 'recruitment',
          period: 'Feb-Mar 2026',
          positions: 10,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-agg-6',
        type: 'change-costs',
        name: 'Marketing & Sales Investment',
        description: 'Aggressive market expansion campaign',
        amount: -30000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(31),
        details: {
          category: 'marketing',
          period: 'Feb-Mar 2026',
          campaign: 'Market Expansion',
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-agg-1',
        userId: '6',
        approverId: '6',
        userName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        status: 'approved',
        timestamp: daysFromNow(-7),
        order: 1,
      },
      {
        id: 'approval-agg-2',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'High risk but high reward — board has visibility, approved',
        timestamp: daysFromNow(-7),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-agg-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'This is the stretch case — 10 new headcount, 3 major contract wins, aggressive marketing. The February/March cash position drops to around £38K at the low point which is below critical threshold. That\'s the honest picture and the board needs to see it.',
        createdAt: daysFromNow(-7),
      },
      {
        id: 'comment-agg-2',
        userId: '1',
        authorName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        text: 'I\'ve been pushing for this kind of ambition and I appreciate the transparency on the cash risk. The £38K low point in March — is that before or after the BigCo first milestone? And do we have any headroom on the overdraft facility?',
        createdAt: daysFromNow(-6),
      },
      {
        id: 'comment-agg-3',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'That\'s before BigCo. If BigCo lands on 45-day terms (as per the updated model) the low point actually improves to around £61K. The overdraft facility gives us £150K headroom — so even at £38K we\'re not drawing on it, but we\'d want to keep it available.',
        createdAt: daysFromNow(-6),
      },
      {
        id: 'comment-agg-4',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'The contracts need to be real pipeline, not aspirational. Contract 1 (£125K) — is that the BigCo second milestone or a separate opportunity? I need to understand whether we\'re double-counting revenue before I can approve this.',
        createdAt: daysFromNow(-5),
      },
      {
        id: 'comment-agg-5',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Separate opportunities — I\'ve been too generic with the names. Contract 1 is the Midlands Highways framework (tender submitted), Contract 2 is the Sheffield Social Housing maintenance contract (preferred bidder), Contract 3 is a private development in Derby (early stage). I\'ll update the descriptions in v2.',
        createdAt: daysFromNow(-4),
      },
      {
        id: 'comment-agg-6',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'That\'s much clearer. Sheffield as preferred bidder is near-certain — I\'d keep that. Midlands Highways is competitive so that\'s genuinely a risk. Derby is too early to model. Suggest v3 removes Derby and models Midlands at 60% probability. Happy to approve v2 in the meantime for board visibility.',
        createdAt: daysFromNow(-3),
      },
    ],
    commentCount: 6,
    versions: [
      {
        id: 'v1-agg',
        version: 1,
        description: 'Initial creation — three generic contract wins, 10 headcount',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-7),
        data: {},
      },
      {
        id: 'v2-agg',
        version: 2,
        description: 'Contract names updated: Midlands Highways, Sheffield Social Housing (preferred bidder), Derby development. No financial changes.',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-4),
        data: {},
      },
      {
        id: 'v3-agg',
        version: 3,
        description: 'Derby development removed per Linda\'s review. Midlands Highways modelled at 60% probability weighting. Low point improves to £52K.',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-1),
        data: {},
      },
    ],
    currentVersion: 3,
    collectionIds: [],
    metrics: {
      minCash: 38000,
      minCashDate: daysFromNow(65),
      runway: 8,
      revenueChange: 20,
      revenueChangeAmount: 305000,
      costChange: 30,
      costChangeAmount: 215000,
      netImpact: 135000,
      endingCash: 382000,
    },
    tags: ['Growth', 'High Risk'],
    auditLog: [
      makeScenarioAudit('scenario-aggressive', '6', 'Sarah Mitchell', 'created', daysAgo(7), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-aggressive', '6', 'Sarah Mitchell', 'shared', daysAgo(7), { detail: 'Shared with Linda Kowalski (view)' }),
      makeScenarioAudit('scenario-aggressive', '6', 'Sarah Mitchell', 'shared', daysAgo(7), { detail: 'Shared with Catherine Hughes (view)' }),
      makeScenarioAudit('scenario-aggressive', '6', 'Sarah Mitchell', 'edited', daysAgo(4), { detail: 'Contract names updated: Midlands Highways, Sheffield Social Housing, Derby development' }),
      makeScenarioAudit('scenario-aggressive', '6', 'Sarah Mitchell', 'edited', daysAgo(1), { detail: 'Derby development removed; Midlands Highways modelled at 60% probability' }),
      makeScenarioAudit('scenario-aggressive', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(7), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-aggressive', '4', 'Linda Kowalski', 'approval-granted', daysAgo(7), { fromStatus: 'pending-approval', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review', detail: 'High risk but high reward — board has visibility, advancing to CEO Sign-off' }),
      makeScenarioAudit('scenario-aggressive', '1', 'Catherine Hughes', 'approval-granted', daysAgo(7), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 2, stageName: 'CEO Sign-off', detail: 'Contracts need to be real pipeline — approved for board visibility' }),
    ],
  },

  // 8. Expand to Scotland - Parked
  {
    id: 'scenario-scotland',
    name: 'Expand to Scotland',
    description: 'Open new office in Edinburgh. Deferred until BigCo acquisition completes and provides working capital.',
    status: 'parked',
    priority: 'none',
    label: null,
    category: 'growth',
    createdBy: '5',
    createdByName: 'Marcus Patel',
    createdAt: daysFromNow(-23), // Jan 6, 2026
    modifiedAt: daysFromNow(-23),
    modifiedBy: 'Marcus Patel',
    shares: [],
    isFavorite: false,
    changes: [
      {
        id: 'change-scotland-1',
        type: 'capital-purchase',
        name: 'Office Setup',
        description: 'Lease deposit, fit-out, equipment for Edinburgh office',
        amount: -150000,
        date: daysFromNow(2), // Feb 2026
        effectiveDate: daysFromNow(2),
        details: {
          item: 'Edinburgh Office',
          breakdown: {
            leaseDeposit: 50000,
            fitOut: 60000,
            equipment: 40000,
          },
        },
        createdAt: daysFromNow(-23),
        createdBy: 'Marcus Patel',
      },
      {
        id: 'change-scotland-2',
        type: 'change-costs',
        name: 'Hiring Costs',
        description: 'Recruitment (£50K) + relocation (£40K) for 10 staff',
        amount: -90000,
        date: daysFromNow(2),
        effectiveDate: daysFromNow(2),
        endDate: daysFromNow(31),
        details: {
          category: 'recruitment',
          period: 'Feb-Mar 2026',
          recruitment: 50000,
          relocation: 40000,
          staff: 10,
        },
        createdAt: daysFromNow(-23),
        createdBy: 'Marcus Patel',
      },
      {
        id: 'change-scotland-3',
        type: 'hire-employees',
        name: 'Payroll - New Team',
        description: '10 staff salaries (engineers, PM, admin)',
        amount: -70000,
        date: daysFromNow(31),
        effectiveDate: daysFromNow(31),
        endDate: daysFromNow(92),
        details: {
          count: 10,
          department: 'Scotland Office',
          period: 'Mar-Apr 2026',
          monthlyCost: 35000,
          totalCost: 70000,
        },
        createdAt: daysFromNow(-23),
        createdBy: 'Marcus Patel',
      },
    ],
    dependencies: [],
    parkedAt: daysFromNow(-23),
    parkedBy: 'Marcus Patel',
    parkedReason: 'Deferred until BigCo acquisition completes and provides working capital.',
    reviewDate: daysFromNow(180), // Review in 6 months
    comments: [
      {
        id: 'comment-scotland-1',
        userId: '5',
        authorName: 'Marcus Patel',
        userRole: 'Regional Finance Director',
        text: 'I\'ve been tracking the Edinburgh construction market for 18 months and the timing is right — several large public sector frameworks are coming to tender in Q3. The issue is purely cash — we need £310K of working capital before we see a return. Parking until the BigCo position is clearer.',
        createdAt: daysFromNow(-23),
      },
      {
        id: 'comment-scotland-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Agreed on the parking. The opportunity is real but the timing is wrong. Let\'s revisit when we have 6 months of BigCo cash in the bank. The -£63K end position is a hard no at current cash levels.',
        createdAt: daysFromNow(-22),
      },
      {
        id: 'comment-scotland-3',
        userId: '5',
        authorName: 'Marcus Patel',
        userRole: 'Regional Finance Director',
        text: 'Understood. I\'ve set the review date for 6 months — if the Aggressive Growth scenario plays out we should have the working capital headroom by then. Worth keeping this live so we don\'t lose the market analysis work.',
        createdAt: daysFromNow(-22),
      },
    ],
    commentCount: 3,
    versions: [
      {
        id: 'v1-scotland',
        version: 1,
        description: 'Initial creation — full Edinburgh office setup and team build',
        createdBy: 'Marcus Patel',
        createdByName: 'Marcus Patel',
        createdAt: daysFromNow(-23),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: -63000,
      minCashDate: daysFromNow(92),
      runway: 4,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: 30,
      costChangeAmount: 310000,
      netImpact: -310000,
      endingCash: -63000,
    },
    tags: ['Expansion', 'Deferred'],
    auditLog: [
      makeScenarioAudit('scenario-scotland', '5', 'Marcus Patel', 'created', daysAgo(23), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-scotland', '5', 'Marcus Patel', 'parked', daysAgo(23), { fromStatus: 'draft', toStatus: 'parked', detail: 'Deferred until BigCo acquisition completes and provides working capital' }),
    ],
  },

  // 9. Leicester City Council Contract - Active
  {
    id: 'scenario-lcc-contract',
    name: 'Leicester City Council Contract',
    description: 'Awarded public sector maintenance contract with Leicester City Council. Three-year framework worth £550K. First phase covers highways maintenance across 12 wards.',
    status: 'active',
    priority: 'high',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-14),
    modifiedAt: daysFromNow(-5),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-lcc-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view',
        sharedAt: daysFromNow(-14),
        sharedBy: 'Sarah Mitchell',
      },
      {
        id: 'share-lcc-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-14),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: true,
    changes: [
      {
        id: 'change-lcc-1',
        type: 'win-customer',
        name: 'Phase 1 Milestone Payment',
        description: 'LCC Phase 1 completion — highways maintenance Q1',
        amount: 92000,
        date: daysFromNow(35),
        effectiveDate: daysFromNow(35),
        details: {
          customer: 'Leicester City Council',
          invoiceDate: daysFromNow(5),
          paymentTerms: 30,
          milestone: 'Phase 1 Completion',
          contractRef: 'LCC-2026-HM-001',
        },
        createdAt: daysFromNow(-14),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-lcc-2',
        type: 'win-customer',
        name: 'Phase 2 Milestone Payment',
        description: 'LCC Phase 2 completion — drainage works',
        amount: 93000,
        date: daysFromNow(75),
        effectiveDate: daysFromNow(75),
        details: {
          customer: 'Leicester City Council',
          invoiceDate: daysFromNow(45),
          paymentTerms: 30,
          milestone: 'Phase 2 Completion',
        },
        createdAt: daysFromNow(-14),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-lcc-3',
        type: 'change-costs',
        name: 'Materials — Highways Maintenance',
        description: 'Asphalt, aggregates and road markings for Q1 programme',
        amount: -45000,
        date: daysFromNow(5),
        effectiveDate: daysFromNow(5),
        endDate: daysFromNow(70),
        details: {
          category: 'materials',
          period: 'Q1 2026',
          monthlyCost: 15000,
          totalCost: 45000,
        },
        createdAt: daysFromNow(-14),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-lcc-4',
        type: 'change-costs',
        name: 'Subcontractor — Drainage Specialists',
        description: 'Specialist drainage subcontractor for Phase 2',
        amount: -28000,
        date: daysFromNow(45),
        effectiveDate: daysFromNow(45),
        endDate: daysFromNow(80),
        details: {
          category: 'subcontractors',
          supplier: 'East Midlands Drainage Ltd',
          period: 'Phase 2',
          totalCost: 28000,
        },
        createdAt: daysFromNow(-14),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-lcc-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Public sector contract — strong cash flow profile. Approved.',
        timestamp: daysFromNow(-10),
        order: 1,
      },
      {
        id: 'approval-lcc-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Strategic win for BuildRight. Excellent reference client.',
        timestamp: daysFromNow(-8),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-lcc-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Contract signed and mobilisation underway. Site teams confirmed for Phase 1 start next Monday.',
        createdAt: daysFromNow(-5),
      },
      {
        id: 'comment-lcc-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Good news. Make sure we invoice promptly at each milestone — LCC typically pays within 25 days when paperwork is clean.',
        createdAt: daysFromNow(-4),
      },
    ],
    commentCount: 2,
    versions: [
      {
        id: 'v1-lcc',
        version: 1,
        description: 'Initial scenario — contract award confirmed',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-14),
        data: {},
      },
      {
        id: 'v2-lcc',
        version: 2,
        description: 'Phase 2 drainage works added following site survey',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-5),
        data: {},
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 220000,
      minCashDate: daysFromNow(10),
      runway: 24,
      revenueChange: 12,
      revenueChangeAmount: 185000,
      costChange: -5,
      costChangeAmount: -73000,
      netImpact: 112000,
      endingCash: 359000,
    },
    tags: ['Public Sector', 'Confirmed', 'Active Contract'],
    auditLog: [
      makeScenarioAudit('scenario-lcc-contract', '6', 'Sarah Mitchell', 'created', daysAgo(14), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-lcc-contract', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(12), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-lcc-contract', '4', 'Linda Kowalski', 'approval-granted', daysAgo(10), { fromStatus: 'pending-approval', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review', detail: 'Confirmed contract — advancing to CEO Sign-off' }),
      makeScenarioAudit('scenario-lcc-contract', '1', 'Catherine Hughes', 'approval-granted', daysAgo(9), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 2, stageName: 'CEO Sign-off', detail: 'Public sector contract — approved' }),
      makeScenarioAudit('scenario-lcc-contract', '6', 'Sarah Mitchell', 'edited', daysAgo(5), { detail: 'Updated Phase 2 payment timing following LCC milestone confirmation' }),
    ],
  },

  // 10. Tender Pipeline — Q2 Live Bids - Under Review
  {
    id: 'scenario-tender-pipeline',
    name: 'Tender Pipeline — Q2 Live Bids',
    description: 'Three live tenders submitted in Q1: Hinckley leisure centre refurbishment (£320K), Loughborough school extension (£185K), and Nottingham road resurfacing framework (£410K). Probability-weighted cash impact modelled across 90 days.',
    status: 'under-review',
    priority: 'high',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-8),
    modifiedAt: daysFromNow(-2),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-tender-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'edit',
        sharedAt: daysFromNow(-8),
        sharedBy: 'Sarah Mitchell',
      },
      {
        id: 'share-tender-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-3),
        sharedBy: 'Linda Kowalski',
      },
      {
        id: 'share-tender-3',
        userId: '5',
        userName: 'Marcus Patel',
        permission: 'comment',
        sharedAt: daysFromNow(-3),
        sharedBy: 'Sarah Mitchell',
      },
      {
        id: 'share-tender-4',
        userId: '3',
        userName: 'Edna Foster',
        permission: 'view',
        sharedAt: daysFromNow(-1),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-tender-1',
        type: 'win-customer',
        name: 'Hinckley Leisure Centre — Probability-Weighted Revenue',
        description: '65% probability × £320K contract = £208K expected value. First milestone payment on award.',
        amount: 135000,
        date: daysFromNow(42),
        effectiveDate: daysFromNow(42),
        details: {
          customer: 'Hinckley & Bosworth Borough Council',
          contractValue: 320000,
          probability: 65,
          expectedValue: 208000,
          milestone: 'Award + Mobilisation',
        },
        createdAt: daysFromNow(-8),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-tender-2',
        type: 'win-customer',
        name: 'Loughborough School Extension — First Invoice',
        description: '80% probability × £185K = £148K expected. Strong relationship with client.',
        amount: 88000,
        date: daysFromNow(60),
        effectiveDate: daysFromNow(60),
        details: {
          customer: 'Charnwood Borough Council',
          contractValue: 185000,
          probability: 80,
          expectedValue: 148000,
          milestone: 'Foundation Works Complete',
        },
        createdAt: daysFromNow(-8),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-tender-3',
        type: 'win-customer',
        name: 'Nottingham Road Framework — Mobilisation Fee',
        description: '45% probability × £410K framework = £184.5K. Competitive tender.',
        amount: 52000,
        date: daysFromNow(85),
        effectiveDate: daysFromNow(85),
        details: {
          customer: 'Nottingham City Council',
          contractValue: 410000,
          probability: 45,
          expectedValue: 184500,
          milestone: 'Framework Award',
        },
        createdAt: daysFromNow(-8),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-tender-4',
        type: 'change-costs',
        name: 'Bid & Mobilisation Costs',
        description: 'Tender preparation, legal review, and mobilisation costs across all three bids',
        amount: -68000,
        date: daysFromNow(5),
        effectiveDate: daysFromNow(5),
        endDate: daysFromNow(50),
        details: {
          category: 'bid-costs',
          breakdown: {
            tenderPrep: 18000,
            legal: 12000,
            mobilisation: 38000,
          },
        },
        createdAt: daysFromNow(-8),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    comments: [
      {
        id: 'comment-tender-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Initial model submitted — three live tenders probability-weighted using our standard methodology. Hinckley at 65% feels right given our existing relationship with Bosworth Council. Nottingham is the wildcard at 45% — first time we\'ve bid for them. Happy to sense-check the assumptions before this goes to Linda.',
        createdAt: daysFromNow(-8),
      },
      {
        id: 'comment-tender-2',
        userId: '5',
        authorName: 'Marcus Patel',
        userRole: 'Regional Manager',
        text: 'Looked through the Hinckley numbers — the 65% probability is fair but the £135K cash timing might be ambitious. Their procurement team typically takes 4–6 weeks post-award before issuing the first instruction. I\'d push the cash receipt out by 3 weeks in v2 to be safe.',
        createdAt: daysFromNow(-7),
      },
      {
        id: 'comment-tender-3',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Good point Marcus — updated Hinckley timing in v2, pushed the receipt from day 42 to day 63. Also split the mobilisation costs into two tranches so they don\'t all hit in week one. The cash low point improves from £76K to £118K as a result.',
        createdAt: daysFromNow(-6),
      },
      {
        id: 'comment-tender-4',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Reviewed v2. Nottingham probability — keep at 45% for now, I\'d rather see the upside modelled. We can run a sensitivity separately. The £38K mobilisation still looks heavy — are we sure that\'s all committed spend? Can any of it be deferred if we don\'t win Nottingham?',
        createdAt: daysFromNow(-4),
      },
      {
        id: 'comment-tender-5',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Checked with ops — £18K tender prep and £12K legal are already committed regardless. The £38K mobilisation is conditional on Nottingham award. Updated v3 to reflect this — mobilisation now only appears in the model if probability threshold is met. Net improvement of ~£38K to the worst-case position.',
        createdAt: daysFromNow(-3),
      },
      {
        id: 'comment-tender-6',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'That\'s much cleaner. Shared with Catherine for visibility ahead of the board pack — she doesn\'t need to action anything, just wants pipeline awareness. V3 is the version to take forward. One last thing: can we add the Loughborough retention clause to the notes? 5% retention on £185K is £9.25K held back — worth flagging even if it\'s not in this model.',
        createdAt: daysFromNow(-3),
      },
      {
        id: 'comment-tender-7',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Retention note added to v4 description. Also flagged the Loughborough payment terms — 30 days from certified invoice, which is better than standard. All three bids now have full payment term commentary in the version notes. Submitting for approval.',
        createdAt: daysFromNow(-2),
      },
      {
        id: 'comment-tender-8',
        userId: '1',
        authorName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        text: 'Thanks for sharing — good visibility of the pipeline. The Nottingham framework would be a significant step for us if it comes through. Worth discussing at the next ops review regardless of outcome.',
        createdAt: daysFromNow(-1),
      },
    ],
    commentCount: 8,
    versions: [
      {
        id: 'v1-tender',
        version: 1,
        description: 'Initial probability-weighted model — Hinckley 65%, Loughborough 80%, Nottingham 45%. All mobilisation costs treated as committed. Cash low point £76K at day 15.',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-8),
        data: {},
      },
      {
        id: 'v2-tender',
        version: 2,
        description: 'Hinckley cash receipt pushed out 3 weeks to day 63 following Marcus\'s note on Bosworth Council procurement timelines. Mobilisation costs split into two tranches (£20K week 1, £18K week 5). Cash low point improves to £118K.',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-6),
        data: {},
      },
      {
        id: 'v3-tender',
        version: 3,
        description: 'Nottingham mobilisation costs (£38K) reclassified as conditional on award — removed from base model. Only committed spend retained (tender prep £18K + legal £12K). Worst-case cash position improves by £38K. Approved by Linda as version to take forward.',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-3),
        data: {},
      },
      {
        id: 'v4-tender',
        version: 4,
        description: 'Final version ahead of approval submission. Loughborough retention clause noted (5% = £9.25K held back until practical completion). Payment terms confirmed: Hinckley 45 days, Loughborough 30 days, Nottingham framework standard 30 days. Shared with Catherine Hughes for board pack visibility.',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-2),
        data: {},
      },
    ],
    currentVersion: 4,
    collectionIds: [],
    metrics: {
      minCash: 210000,
      minCashDate: daysFromNow(15),
      runway: 22,
      revenueChange: 15,
      revenueChangeAmount: 275000,
      costChange: -4,
      costChangeAmount: -68000,
      netImpact: 207000,
      endingCash: 454000,
    },
    tags: ['Tender', 'Pipeline', 'Q2'],
    auditLog: [
      makeScenarioAudit('scenario-tender-pipeline', '6', 'Sarah Mitchell', 'created', daysAgo(8), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-tender-pipeline', '6', 'Sarah Mitchell', 'status-changed', daysAgo(6), { fromStatus: 'draft', toStatus: 'under-review', detail: 'Sent for peer review — probability weightings need sign-off' }),
    ],
  },

  // 11. Midlands JV — Crossroads Development - Pending Approval
  {
    id: 'scenario-jv-midlands',
    name: 'Midlands JV — Crossroads Development',
    description: 'Joint venture with Crossroads Property Group to deliver 85-unit residential scheme in Coventry. BuildRight provides construction management and groundworks. 50/50 profit share on £2.1M project. Significant upfront capital requirement offset by phased developer payments.',
    status: 'pending-approval',
    currentStageOrder: 2,
    currentStageName: 'CEO Sign-off',
    approvalWorkflowName: 'Standard Approval',
    priority: 'high',
    label: null,
    category: 'growth',
    createdBy: '5',
    createdByName: 'Marcus Patel',
    createdAt: daysFromNow(-18),
    modifiedAt: daysFromNow(-3),
    modifiedBy: 'Marcus Patel',
    shares: [
      {
        id: 'share-jv-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'comment',
        sharedAt: daysFromNow(-18),
        sharedBy: 'Marcus Patel',
      },
      {
        id: 'share-jv-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-18),
        sharedBy: 'Marcus Patel',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-jv-1',
        type: 'win-customer',
        name: 'JV Revenue Share — Phase 1 Completion',
        description: 'BuildRight 50% share of Phase 1 developer payment (£840K total)',
        amount: 210000,
        date: daysFromNow(55),
        effectiveDate: daysFromNow(55),
        details: {
          customer: 'Crossroads Property Group',
          jvStructure: '50/50 profit share',
          totalPhaseValue: 840000,
          buildRightShare: 420000,
          milestone: 'Phase 1 Foundations & Frame',
          note: 'Payment split across 2 tranches',
        },
        createdAt: daysFromNow(-18),
        createdBy: 'Marcus Patel',
      },
      {
        id: 'change-jv-2',
        type: 'win-customer',
        name: 'JV Revenue Share — Phase 2 Milestone',
        description: 'BuildRight 50% share of Phase 2 superstructure payment',
        amount: 210000,
        date: daysFromNow(88),
        effectiveDate: daysFromNow(88),
        details: {
          customer: 'Crossroads Property Group',
          totalPhaseValue: 840000,
          buildRightShare: 420000,
          milestone: 'Phase 2 Superstructure',
        },
        createdAt: daysFromNow(-18),
        createdBy: 'Marcus Patel',
      },
      {
        id: 'change-jv-3',
        type: 'capital-purchase',
        name: 'JV Setup & Working Capital Contribution',
        description: 'BuildRight capital injection into JV: legal setup, working capital, mobilisation',
        amount: -165000,
        date: daysFromNow(8),
        effectiveDate: daysFromNow(8),
        details: {
          item: 'JV Capital Contribution',
          breakdown: {
            legalSetup: 25000,
            workingCapital: 100000,
            mobilisation: 40000,
          },
        },
        createdAt: daysFromNow(-18),
        createdBy: 'Marcus Patel',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-jv-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved' as const,
        comments: 'Capital requirement is significant but returns are strong. Recommend CEO sign-off given JV structure.',
        timestamp: daysFromNow(-3),
        order: 1,
      },
      {
        id: 'approval-jv-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'pending' as const,
        timestamp: daysFromNow(-3),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-jv-1',
        userId: '5',
        authorName: 'Marcus Patel',
        userRole: 'Regional Finance Director',
        text: 'Crossroads have a strong track record — 12 completed schemes in the East Midlands. Their legal team have reviewed and approved the JV heads of terms. Waiting on Catherine\'s final sign-off.',
        createdAt: daysFromNow(-3),
      },
      {
        id: 'comment-jv-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'I\'ve approved from a finance perspective. The £165K upfront is manageable given our cash position. Catherine — this is a good opportunity but needs your strategic view on the JV model.',
        createdAt: daysFromNow(-2),
      },
    ],
    commentCount: 2,
    versions: [
      {
        id: 'v1-jv',
        version: 1,
        description: 'Initial JV model',
        createdBy: 'Marcus Patel',
        createdByName: 'Marcus Patel',
        createdAt: daysFromNow(-18),
        data: {},
      },
      {
        id: 'v2-jv',
        version: 2,
        description: 'Updated following Crossroads legal review — payment schedule revised',
        createdBy: 'Marcus Patel',
        createdByName: 'Marcus Patel',
        createdAt: daysFromNow(-5),
        data: {},
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 82000,
      minCashDate: daysFromNow(12),
      runway: 14,
      revenueChange: 28,
      revenueChangeAmount: 420000,
      costChange: -11,
      costChangeAmount: -165000,
      netImpact: 255000,
      endingCash: 502000,
    },
    tags: ['JV', 'Residential', 'Coventry'],
    auditLog: [
      makeScenarioAudit('scenario-jv-midlands', '5', 'Marcus Patel', 'created', daysAgo(18), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-jv-midlands', '5', 'Marcus Patel', 'submitted-for-approval', daysAgo(15), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-jv-midlands', '4', 'Linda Kowalski', 'approval-granted', daysAgo(10), { fromStatus: 'pending-approval', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review', detail: 'JV terms reviewed — advancing to CEO Sign-off' }),
    ],
  },

  // 12. Subcontractor Failure — Apex Electrics - Draft
  {
    id: 'scenario-subcontractor-failure',
    name: 'Subcontractor Failure — Apex Electrics',
    description: 'Apex Electrics (key M&E subcontractor on 3 live projects) has entered administration. Modelling the cash impact of sourcing replacement subcontractors at short notice, delayed milestone payments, and potential retention withheld by main contractors.',
    status: 'pending-approval',
    currentStageOrder: 1,
    currentStageName: 'Finance Review',
    approvalWorkflowName: 'Standard Approval',
    priority: 'critical',
    label: null,
    category: 'crisis',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-4),
    modifiedAt: daysFromNow(-1),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-sub-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view' as const,
        sharedAt: daysFromNow(-1),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-sub-1',
        type: 'change-costs',
        name: 'Emergency Replacement Subcontractor Premium',
        description: 'Replacement M&E contractor at short notice — 35% premium on original Apex rates across 3 projects',
        amount: -68000,
        date: daysFromNow(5),
        effectiveDate: daysFromNow(5),
        endDate: daysFromNow(75),
        details: {
          category: 'subcontractors',
          originalContractor: 'Apex Electrics',
          replacementPremium: 35,
          affectedProjects: ['Meridian Phase 2', 'Hinckley Leisure', 'LCC Phase 2'],
          monthlyCost: 22667,
        },
        createdAt: daysFromNow(-2),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-sub-2',
        type: 'lose-customer',
        name: 'Delayed Milestone Payments — 3 Projects',
        description: 'Main contractors withholding milestone payments pending M&E works completion. Estimated 6-week delay.',
        amount: -42000,
        date: daysFromNow(30),
        effectiveDate: daysFromNow(30),
        details: {
          reason: 'M&E Works Delay',
          delayWeeks: 6,
          affectedMilestones: 3,
          estimatedRecovery: daysFromNow(72),
        },
        createdAt: daysFromNow(-2),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-sub-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'pending' as const,
        timestamp: daysFromNow(-1),
        order: 1,
      },
      {
        id: 'approval-sub-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'pending' as const,
        timestamp: daysFromNow(-1),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-sub-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Still gathering exact exposure figures. The £68K replacement cost is based on Apex\'s last 3 invoices with a 35% uplift. May need revising once we\'ve spoken to replacement contractors. Submitting now so leadership are aware — this is urgent.',
        createdAt: daysFromNow(-1),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-sub',
        version: 1,
        description: 'Initial crisis assessment — figures TBC pending contractor quotes',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-4),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 95000,
      minCashDate: daysFromNow(35),
      runway: 10,
      revenueChange: -5,
      revenueChangeAmount: -42000,
      costChange: -8,
      costChangeAmount: -68000,
      netImpact: -110000,
      endingCash: 137000,
    },
    tags: ['Crisis', 'Subcontractor', 'Urgent'],
    auditLog: [
      makeScenarioAudit('scenario-subcontractor-failure', '6', 'Sarah Mitchell', 'created', daysAgo(4), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-subcontractor-failure', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(1), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review', detail: 'Submitting urgently — leadership awareness required immediately' }),
    ],
    issues: [
      {
        id: 'issue-sub-1',
        type: 'start-date-passed' as const,
        severity: 'warning' as const,
        title: 'Scenario start date has passed',
        description: `This scenario's earliest adjustment effective date (${format(daysAgo(2), 'd MMM yyyy')}) has already passed. The scenario is currently pending approval but real-world events may already be unfolding. Review whether the adjustment dates need updating to reflect the current situation.`,
        detectedAt: hoursFromNow(-2),
        impactDescription: 'Adjustment dates may no longer reflect actual timeline',
      },
    ],
  },

  // 13. Material Cost Spike — Steel & Timber - Active
  {
    id: 'scenario-material-costs',
    name: 'Material Cost Spike — Steel & Timber +22%',
    description: 'Steel and structural timber prices have increased 22% due to supply chain disruption. Modelling the impact on all live projects and tenders where materials are not yet procured under fixed-price agreements.',
    status: 'active',
    priority: 'high',
    label: null,
    category: 'crisis',
    createdBy: '4',
    createdByName: 'Linda Kowalski',
    createdAt: daysFromNow(-10),
    modifiedAt: daysFromNow(-6),
    modifiedBy: 'Linda Kowalski',
    shares: [
      {
        id: 'share-mat-1',
        userId: '6',
        userName: 'Sarah Mitchell',
        permission: 'view',
        sharedAt: daysFromNow(-10),
        sharedBy: 'Linda Kowalski',
      },
      {
        id: 'share-mat-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-10),
        sharedBy: 'Linda Kowalski',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-mat-1',
        type: 'change-costs',
        name: 'Steel Price Increase — Live Projects',
        description: '22% uplift on structural steel across 4 live projects. Fixed-price contracts absorb cost.',
        amount: -54000,
        date: daysFromNow(3),
        effectiveDate: daysFromNow(3),
        endDate: daysFromNow(92),
        details: {
          category: 'materials',
          material: 'Structural Steel',
          priceIncrease: 22,
          affectedProjects: 4,
          monthlyCost: 18000,
        },
        createdAt: daysFromNow(-10),
        createdBy: 'Linda Kowalski',
      },
      {
        id: 'change-mat-2',
        type: 'change-costs',
        name: 'Structural Timber Price Increase',
        description: '22% uplift on structural timber orders — primarily affecting Loughborough school extension.',
        amount: -38000,
        date: daysFromNow(3),
        effectiveDate: daysFromNow(3),
        endDate: daysFromNow(92),
        details: {
          category: 'materials',
          material: 'Structural Timber',
          priceIncrease: 22,
          affectedProjects: 2,
          monthlyCost: 12667,
        },
        createdAt: daysFromNow(-10),
        createdBy: 'Linda Kowalski',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-mat-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Approved for active planning. Procurement team to investigate forward purchasing to lock in current rates.',
        timestamp: daysFromNow(-6),
        order: 1,
      },
      {
        id: 'approval-mat-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Noted. Are we able to pass any of this through to clients on variation orders?',
        timestamp: daysFromNow(-5),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-mat-1',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Catherine — on your question about variation orders: we can claim on 3 of the 4 affected contracts. The LCC contract has a price fluctuation clause. Estimated recovery of £18K-£22K via VOs. Net impact therefore closer to -£70K in worst case.',
        createdAt: daysFromNow(-4),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-mat',
        version: 1,
        description: 'Initial cost impact model',
        createdBy: 'Linda Kowalski',
        createdByName: 'Linda Kowalski',
        createdAt: daysFromNow(-10),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 155000,
      minCashDate: daysFromNow(92),
      runway: 16,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: -6,
      costChangeAmount: -92000,
      netImpact: -92000,
      endingCash: 155000,
    },
    tags: ['Materials', 'Supply Chain', 'Cost Pressure'],
    auditLog: [
      makeScenarioAudit('scenario-material-costs', '4', 'Linda Kowalski', 'created', daysAgo(10), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-material-costs', '4', 'Linda Kowalski', 'submitted-for-approval', daysAgo(8), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-material-costs', '6', 'Sarah Mitchell', 'approval-granted', daysAgo(7), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 1, stageName: 'Finance Review', detail: 'Steel and timber cost impact confirmed — approved' }),
    ],
  },

  // 14. Payment Dispute — Worthington Group - Under Review
  {
    id: 'scenario-payment-dispute',
    name: 'Payment Dispute — Worthington Group',
    description: 'Worthington Group (main contractor on Birmingham civic centre project) is withholding £128K in certified payments, disputing the value of variations 14-19. Legal opinion obtained. Modelling worst-case cash impact if dispute extends beyond 90 days.',
    status: 'under-review',
    priority: 'critical',
    label: null,
    category: 'crisis',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-6),
    modifiedAt: daysFromNow(-1),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-dispute-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'comment',
        sharedAt: daysFromNow(-6),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-dispute-1',
        type: 'lose-customer',
        name: 'Withheld Certified Payments — Variations 14-19',
        description: 'Worthington withholding payment on certified variations. Legal action initiated under HGCRA adjudication.',
        amount: -128000,
        date: daysFromNow(1),
        effectiveDate: daysFromNow(1),
        details: {
          customer: 'Worthington Group',
          project: 'Birmingham Civic Centre',
          disputedVariations: '14-19',
          certifiedValue: 128000,
          legalRoute: 'HGCRA Adjudication',
          expectedResolution: daysFromNow(84),
        },
        createdAt: daysFromNow(-6),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    comments: [
      {
        id: 'comment-dispute-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Sent to Linda for review. Our solicitors (Gateley\'s) believe we have a strong case under the adjudication route — 28-day decision expected. Cash impact is significant in the short term either way.',
        createdAt: daysFromNow(-1),
      },
      {
        id: 'comment-dispute-2',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Reviewing. Have we explored a without-prejudice settlement? £95K in the hand vs £128K in 84 days might be worth considering given our cash position. Ask Gateley\'s to advise.',
        createdAt: daysFromNow(0),
      },
    ],
    commentCount: 2,
    versions: [
      {
        id: 'v1-dispute',
        version: 1,
        description: 'Worst-case — full amount withheld for 90 days',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-6),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 85000,
      minCashDate: daysFromNow(45),
      runway: 8,
      revenueChange: -8,
      revenueChangeAmount: -128000,
      costChange: 0,
      costChangeAmount: 0,
      netImpact: -128000,
      endingCash: 119000,
    },
    tags: ['Dispute', 'Legal', 'Critical'],
    auditLog: [
      makeScenarioAudit('scenario-payment-dispute', '6', 'Sarah Mitchell', 'created', daysAgo(6), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-payment-dispute', '6', 'Sarah Mitchell', 'status-changed', daysAgo(4), { fromStatus: 'draft', toStatus: 'under-review', detail: 'Legal input required before progressing to approval' }),
    ],
    issues: [
      {
        id: 'issue-dispute-1',
        type: 'base-forecast-changed' as const,
        severity: 'warning' as const,
        title: 'Base forecast has shifted since this scenario was created',
        description: `The base forecast has changed by £28,500 since this scenario was last reviewed on ${format(daysAgo(4), 'd MMM yyyy')}. A data source sync from Access Financials has updated the outstanding debtor position for Worthington Group, which affects the baseline against which this scenario's adjustments are calculated.`,
        detectedAt: hoursFromNow(-4),
        impactAmount: -28500,
        impactDescription: `-£28,500 shift in base forecast — review net impact figures`,
      },
    ],
  },

  // 15. Project Overrun — Meridian Phase 2 - Draft
  {
    id: 'scenario-overrun-meridian',
    name: 'Project Overrun — Meridian Phase 2',
    description: 'Meridian retail park Phase 2 is running 6 weeks behind programme due to ground condition surveys revealing unexpected contamination. Modelling additional labour, extended plant hire, and delayed completion payment from client.',
    status: 'draft',
    priority: 'high',
    label: null,
    category: 'crisis',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-4),
    modifiedAt: daysFromNow(-4),
    modifiedBy: 'Sarah Mitchell',
    shares: [],
    isFavorite: false,
    changes: [
      {
        id: 'change-overrun-1',
        type: 'change-costs',
        name: 'Additional Labour — Remediation Works',
        description: '6-week remediation programme, 12 operatives at overtime rates',
        amount: -46000,
        date: daysFromNow(7),
        effectiveDate: daysFromNow(7),
        endDate: daysFromNow(49),
        details: {
          category: 'labour',
          programme: 'Ground Remediation',
          duration: '6 weeks',
          operatives: 12,
          overtimeRate: true,
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-overrun-2',
        type: 'change-costs',
        name: 'Extended Plant Hire',
        description: 'Extended hire of excavators and specialist remediation equipment for 6 weeks',
        amount: -18000,
        date: daysFromNow(7),
        effectiveDate: daysFromNow(7),
        endDate: daysFromNow(49),
        details: {
          category: 'plant',
          equipment: 'Excavators + Remediation Plant',
          weeklyCost: 3000,
          weeks: 6,
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-overrun-3',
        type: 'lose-customer',
        name: 'Delayed Completion Payment',
        description: 'Practical completion delayed by 6 weeks — final 10% retention payment delayed accordingly',
        amount: -38000,
        date: daysFromNow(42),
        effectiveDate: daysFromNow(42),
        details: {
          customer: 'Meridian Asset Management',
          originalCompletionDate: daysFromNow(0),
          revisedCompletionDate: daysFromNow(42),
          delayWeeks: 6,
          retentionValue: 38000,
        },
        createdAt: daysFromNow(-4),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    comments: [],
    commentCount: 0,
    versions: [
      {
        id: 'v1-overrun',
        version: 1,
        description: 'Initial overrun assessment — awaiting full contamination survey report',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-4),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 112000,
      minCashDate: daysFromNow(50),
      runway: 11,
      revenueChange: -2,
      revenueChangeAmount: -38000,
      costChange: -4,
      costChangeAmount: -64000,
      netImpact: -102000,
      endingCash: 145000,
    },
    tags: ['Overrun', 'Contamination', 'Meridian'],
    auditLog: [
      makeScenarioAudit('scenario-overrun-meridian', '6', 'Sarah Mitchell', 'created', daysAgo(4), { toStatus: 'draft' }),
    ],
  },

  // 16. Hire 8 Site Operatives — H2 Expansion - Active
  {
    id: 'scenario-hire-operatives',
    name: 'Hire 8 Site Operatives — H2 Expansion',
    description: 'Planned headcount expansion to support H2 contract pipeline. 8 experienced site operatives (groundworks and civils) to be recruited ahead of anticipated contract wins. Staggered start dates across April-May to manage payroll ramp.',
    status: 'active',
    priority: 'high',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-12),
    modifiedAt: daysFromNow(-7),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-hire-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'comment',
        sharedAt: daysFromNow(-12),
        sharedBy: 'Sarah Mitchell',
      },
      {
        id: 'share-hire-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-12),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-hire-op-1',
        type: 'hire-employees',
        name: 'Batch 1 — 4 Operatives (April Start)',
        description: '4 groundworks operatives starting April 1. CIS subcontract basis initially.',
        amount: -39600,
        date: daysFromNow(62),
        effectiveDate: daysFromNow(62),
        endDate: daysFromNow(92),
        details: {
          count: 4,
          department: 'Site Operations — Groundworks',
          startDate: daysFromNow(62),
          basis: 'CIS Subcontract',
          monthlyCost: 13200,
          ratePerPerson: 3300,
        },
        createdAt: daysFromNow(-12),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-op-2',
        type: 'hire-employees',
        name: 'Batch 2 — 4 Operatives (May Start)',
        description: '4 civils operatives starting May 1.',
        amount: -13200,
        date: daysFromNow(92),
        effectiveDate: daysFromNow(92),
        endDate: daysFromNow(92),
        details: {
          count: 4,
          department: 'Site Operations — Civils',
          startDate: daysFromNow(92),
          basis: 'PAYE',
          monthlyCost: 13200,
        },
        createdAt: daysFromNow(-12),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-op-3',
        type: 'change-costs',
        name: 'Recruitment & PPE',
        description: 'Recruitment agency fees (£12K) and PPE/tools per operative (£800 × 8)',
        amount: -18400,
        date: daysFromNow(30),
        effectiveDate: daysFromNow(30),
        details: {
          category: 'recruitment',
          agencyFees: 12000,
          ppePerPerson: 800,
          operatives: 8,
          totalPpe: 6400,
        },
        createdAt: daysFromNow(-12),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-hire-op-4',
        type: 'win-customer',
        name: 'Additional Capacity Revenue — Q2 Contracts',
        description: 'Revenue from contracts won using expanded team capacity. Conservative estimate based on 75% utilisation.',
        amount: 198000,
        date: daysFromNow(75),
        effectiveDate: daysFromNow(75),
        details: {
          customer: 'Pipeline Contracts',
          utilisation: 75,
          revenuePerOperative: 24750,
          operatives: 8,
        },
        createdAt: daysFromNow(-12),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-hire-op-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Approved subject to H2 pipeline materialising. Batch 2 to be reviewed in April before onboarding.',
        timestamp: daysFromNow(-7),
        order: 1,
      },
      {
        id: 'approval-hire-op-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Approved. Review Batch 2 decision in April — pipeline should be clearer by then.',
        timestamp: daysFromNow(-6),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-hire-op-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Both batches now approved. I\'ll coordinate with HR on the April recruitment campaign. Starting CIS for Batch 1 gives us flexibility if the pipeline doesn\'t fully materialise.',
        createdAt: daysFromNow(-5),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-hire-op',
        version: 1,
        description: 'Initial headcount plan — single batch',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-12),
        data: {},
      },
      {
        id: 'v2-hire-op',
        version: 2,
        description: 'Split into two batches on Linda\'s recommendation — reduces risk',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-8),
        data: {},
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 215000,
      minCashDate: daysFromNow(65),
      runway: 22,
      revenueChange: 13,
      revenueChangeAmount: 198000,
      costChange: -5,
      costChangeAmount: -71200,
      netImpact: 126800,
      endingCash: 373800,
    },
    tags: ['Headcount', 'Expansion', 'H2 Planning'],
    auditLog: [
      makeScenarioAudit('scenario-hire-operatives', '6', 'Sarah Mitchell', 'created', daysAgo(12), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-hire-operatives', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(10), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-hire-operatives', '4', 'Linda Kowalski', 'approval-granted', daysAgo(8), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 1, stageName: 'Finance Review', detail: 'H2 headcount plan approved' }),
    ],
  },

  // 17. Plant Purchase vs Lease Analysis - Draft
  {
    id: 'scenario-plant-purchase',
    name: 'Plant Purchase vs Lease — JCB 3CX Fleet',
    description: 'BuildRight currently leases 4 JCB 3CX backhoe loaders at £2,100/month each (£100,800/year). Analysing the cash flow impact of purchasing 2 units outright at £42,500 each, reducing ongoing lease costs. Breakeven at 34 months.',
    status: 'draft',
    priority: 'medium',
    label: null,
    category: 'cost-cutting',
    createdBy: '4',
    createdByName: 'Linda Kowalski',
    createdAt: daysFromNow(-9),
    modifiedAt: daysFromNow(-9),
    modifiedBy: 'Linda Kowalski',
    shares: [
      {
        id: 'share-plant-1',
        userId: '6',
        userName: 'Sarah Mitchell',
        permission: 'view',
        sharedAt: daysFromNow(-9),
        sharedBy: 'Linda Kowalski',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-plant-1',
        type: 'capital-purchase',
        name: 'Purchase 2 × JCB 3CX Backhoe Loaders',
        description: 'Outright purchase of 2 units at £42,500 each. Replaces 2 of the 4 current leases.',
        amount: -85000,
        date: daysFromNow(20),
        effectiveDate: daysFromNow(20),
        details: {
          item: 'JCB 3CX Backhoe Loader × 2',
          unitCost: 42500,
          units: 2,
          supplier: 'Flannery Plant Hire',
          warrantyYears: 3,
        },
        createdAt: daysFromNow(-9),
        createdBy: 'Linda Kowalski',
      },
      {
        id: 'change-plant-2',
        type: 'change-costs',
        name: 'Lease Cost Saving — 2 Units Cancelled',
        description: 'Saving on 2 lease agreements at £2,100/month each from month 2 onwards',
        amount: 24000,
        date: daysFromNow(30),
        effectiveDate: daysFromNow(30),
        endDate: daysFromNow(92),
        details: {
          category: 'plant',
          monthlySaving: 4200,
          unitsReduced: 2,
          annualisedSaving: 50400,
        },
        createdAt: daysFromNow(-9),
        createdBy: 'Linda Kowalski',
      },
    ],
    dependencies: [],
    comments: [
      {
        id: 'comment-plant-1',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Short-term cash hit of £61K net but annual saving of £50.4K. Breakeven in 34 months. Assumes 85% utilisation — if we dip below 70% the lease model is more attractive. Sarah, can you pull utilisation stats for the last 12 months?',
        createdAt: daysFromNow(-9),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-plant',
        version: 1,
        description: 'Initial purchase vs lease analysis',
        createdBy: 'Linda Kowalski',
        createdByName: 'Linda Kowalski',
        createdAt: daysFromNow(-9),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 162000,
      minCashDate: daysFromNow(22),
      runway: 14,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: -4,
      costChangeAmount: -61000,
      netImpact: -61000,
      endingCash: 186000,
    },
    tags: ['Plant', 'Capital', 'Cost Efficiency'],
    auditLog: [
      makeScenarioAudit('scenario-plant-purchase', '4', 'Linda Kowalski', 'created', daysAgo(9), { toStatus: 'draft' }),
    ],
  },

  // 18. Apprenticeship Levy Optimisation - Active
  {
    id: 'scenario-apprenticeship',
    name: 'Apprenticeship Levy Optimisation',
    description: 'BuildRight pays £22,500/year into the apprenticeship levy but has historically claimed less than 40% back. Modelling the cash benefit of a structured programme: 4 apprentices (groundworks L2) starting April, maximising levy draw-down before expiry.',
    status: 'active',
    priority: 'medium',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-15),
    modifiedAt: daysFromNow(-11),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-app-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view',
        sharedAt: daysFromNow(-15),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-app-1',
        type: 'win-customer',
        name: 'Apprenticeship Levy Draw-Down',
        description: 'Reclaim levy contributions via 4 × L2 Groundworks apprenticeships. Draw-down commences April.',
        amount: 28500,
        date: daysFromNow(62),
        effectiveDate: daysFromNow(62),
        details: {
          scheme: 'Apprenticeship Levy',
          apprentices: 4,
          standard: 'Groundworks L2',
          monthlyDrawdown: 2375,
          levyBalance: 67500,
          expiryDate: daysFromNow(365),
        },
        createdAt: daysFromNow(-15),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-app-2',
        type: 'change-costs',
        name: 'Training Provider & Supervision Costs',
        description: 'Off-the-job training admin, CITB assessment fees, and supervisor time allocation',
        amount: -12000,
        date: daysFromNow(62),
        effectiveDate: daysFromNow(62),
        endDate: daysFromNow(92),
        details: {
          category: 'training',
          trainingProvider: 'Leicester College',
          assessmentFees: 4800,
          supervisorTime: 7200,
        },
        createdAt: daysFromNow(-15),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-app-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Good use of levy balance. We\'ve been leaving money on the table. Approved.',
        timestamp: daysFromNow(-11),
        order: 1,
      },
      {
        id: 'approval-app-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Strongly support — good for the business and good for the community.',
        timestamp: daysFromNow(-10),
        order: 2,
      },
    ],
    comments: [],
    commentCount: 0,
    versions: [
      {
        id: 'v1-app',
        version: 1,
        description: 'Initial levy optimisation model',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-15),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 240000,
      minCashDate: daysFromNow(65),
      runway: 26,
      revenueChange: 2,
      revenueChangeAmount: 28500,
      costChange: -1,
      costChangeAmount: -12000,
      netImpact: 16500,
      endingCash: 263500,
    },
    tags: ['Levy', 'Apprenticeship', 'CITB'],
    auditLog: [
      makeScenarioAudit('scenario-apprenticeship', '6', 'Sarah Mitchell', 'created', daysAgo(15), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-apprenticeship', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(13), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-apprenticeship', '4', 'Linda Kowalski', 'approval-granted', daysAgo(11), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 1, stageName: 'Finance Review', detail: 'Levy optimisation approved' }),
    ],
  },

  // 19. Interest Rate Rise +0.5% - Active
  {
    id: 'scenario-interest-rate',
    name: 'Interest Rate Rise +0.5%',
    description: 'Bank of England base rate increase of 0.5% impacts BuildRight\'s variable rate overdraft facility (£180K limit, currently £95K drawn) and hire purchase agreements on plant. Modelling ongoing cost drag and impact on project financing.',
    status: 'active',
    priority: 'medium',
    label: null,
    category: 'crisis',
    createdBy: '4',
    createdByName: 'Linda Kowalski',
    createdAt: daysFromNow(-7),
    modifiedAt: daysFromNow(-7),
    modifiedBy: 'Linda Kowalski',
    shares: [
      {
        id: 'share-ir-1',
        userId: '6',
        userName: 'Sarah Mitchell',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Linda Kowalski',
      },
      {
        id: 'share-ir-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-7),
        sharedBy: 'Linda Kowalski',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-ir-1',
        type: 'change-costs',
        name: 'Overdraft Interest Cost Increase',
        description: '+0.5% on £95K drawn balance = £475/year additional interest cost. Monthly impact £39.60.',
        amount: -3600,
        date: daysFromNow(1),
        effectiveDate: daysFromNow(1),
        endDate: daysFromNow(92),
        details: {
          category: 'finance',
          facility: 'NatWest Overdraft',
          facilityLimit: 180000,
          drawnBalance: 95000,
          rateIncrease: 0.5,
          annualCost: 475,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Linda Kowalski',
      },
      {
        id: 'change-ir-2',
        type: 'change-costs',
        name: 'Hire Purchase Agreement — Rate Uplift',
        description: '+0.5% on variable rate HP agreements for plant fleet. 6 agreements affected.',
        amount: -18900,
        date: daysFromNow(1),
        effectiveDate: daysFromNow(1),
        endDate: daysFromNow(92),
        details: {
          category: 'finance',
          agreements: 6,
          totalOutstanding: 420000,
          rateIncrease: 0.5,
          annualCost: 2100,
          quarterlyImpact: 525,
        },
        createdAt: daysFromNow(-7),
        createdBy: 'Linda Kowalski',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-ir-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Relatively modest impact. Activated for planning — will monitor if further rate rises materialise.',
        timestamp: daysFromNow(-5),
        order: 1,
      },
      {
        id: 'approval-ir-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Noted. Can we fix any of the HP rates?',
        timestamp: daysFromNow(-4),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-ir-1',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'Catherine — on the HP rates: 2 of the 6 agreements allow a fixed rate conversion. I\'m in discussion with Close Brothers about locking in on those two. Will save approximately £6K/year if rates rise further.',
        createdAt: daysFromNow(-3),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-ir',
        version: 1,
        description: 'Initial rate rise impact model',
        createdBy: 'Linda Kowalski',
        createdByName: 'Linda Kowalski',
        createdAt: daysFromNow(-7),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 210000,
      minCashDate: daysFromNow(92),
      runway: 20,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: -1,
      costChangeAmount: -22500,
      netImpact: -22500,
      endingCash: 224500,
    },
    tags: ['Interest Rate', 'Finance Costs', 'Bank of England'],
    auditLog: [
      makeScenarioAudit('scenario-interest-rate', '4', 'Linda Kowalski', 'created', daysAgo(7), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-interest-rate', '4', 'Linda Kowalski', 'submitted-for-approval', daysAgo(6), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-interest-rate', '6', 'Sarah Mitchell', 'approval-granted', daysAgo(6), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 1, stageName: 'Finance Review', detail: 'Interest rate sensitivity approved for board visibility' }),
    ],
  },

  // 20. Fuel Cost Escalation - Committed
  {
    id: 'scenario-fuel-escalation',
    name: 'Fuel Cost Escalation — Fleet Impact',
    description: 'Diesel prices up 18% since Q4 budget was set. BuildRight operates 14 commercial vehicles (vans, plant transport, site managers\' vehicles). Decision made to absorb the cost increase rather than pass on to clients on fixed-price contracts. Committed to planning.',
    status: 'active',
    priority: 'medium',
    label: null,
    category: 'crisis',
    createdBy: '4',
    createdByName: 'Linda Kowalski',
    createdAt: daysFromNow(-20),
    modifiedAt: daysFromNow(-12),
    modifiedBy: 'Linda Kowalski',
    shares: [
      {
        id: 'share-fuel-1',
        userId: '6',
        userName: 'Sarah Mitchell',
        permission: 'view',
        sharedAt: daysFromNow(-20),
        sharedBy: 'Linda Kowalski',
      },
      {
        id: 'share-fuel-2',
        userId: '1',
        userName: 'Catherine Hughes',
        permission: 'view',
        sharedAt: daysFromNow(-20),
        sharedBy: 'Linda Kowalski',
      },
    ],
    isFavorite: false,
    changes: [
      {
        id: 'change-fuel-1',
        type: 'change-costs',
        name: 'Fleet Fuel Cost Increase — 18% Uplift',
        description: '14 vehicles × average £820/month fuel × 18% increase = £2,066/month additional cost',
        amount: -34500,
        date: daysFromNow(1),
        effectiveDate: daysFromNow(1),
        endDate: daysFromNow(92),
        details: {
          category: 'fleet',
          vehicles: 14,
          averageMonthlyFuelPerVehicle: 820,
          priceIncrease: 18,
          monthlyIncrease: 2066,
          quarterlyImpact: 6198,
        },
        createdAt: daysFromNow(-20),
        createdBy: 'Linda Kowalski',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-fuel-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'Approved and committed. Looking at telematics and route optimisation to offset partially.',
        timestamp: daysFromNow(-14),
        order: 1,
      },
      {
        id: 'approval-fuel-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Committed. Explore fuel cards with better rates — speak to Allstar.',
        timestamp: daysFromNow(-12),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-fuel-1',
        userId: '4',
        authorName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        text: 'This is now committed to our plan. The £34.5K impact is real and baked in. Sarah — please make sure the Q2 budget reforecast includes this. I\'ve initiated a conversation with Allstar Fuel Cards and should have better rates from next month.',
        createdAt: daysFromNow(-10),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-fuel',
        version: 1,
        description: 'Initial fuel cost model — 15% uplift assumed',
        createdBy: 'Linda Kowalski',
        createdByName: 'Linda Kowalski',
        createdAt: daysFromNow(-20),
        data: {},
      },
      {
        id: 'v2-fuel',
        version: 2,
        description: 'Updated to 18% uplift following January pump price data',
        createdBy: 'Linda Kowalski',
        createdByName: 'Linda Kowalski',
        createdAt: daysFromNow(-12),
        data: {},
      },
    ],
    currentVersion: 2,
    collectionIds: [],
    metrics: {
      minCash: 198000,
      minCashDate: daysFromNow(92),
      runway: 18,
      revenueChange: 0,
      revenueChangeAmount: 0,
      costChange: -2,
      costChangeAmount: -34500,
      netImpact: -34500,
      endingCash: 212500,
    },
    tags: ['Fuel', 'Fleet'],
    auditLog: [
      makeScenarioAudit('scenario-fuel-escalation', '4', 'Linda Kowalski', 'created', daysAgo(20), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-fuel-escalation', '4', 'Linda Kowalski', 'submitted-for-approval', daysAgo(18), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-fuel-escalation', '6', 'Sarah Mitchell', 'approval-granted', daysAgo(17), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 1, stageName: 'Finance Review', detail: 'Fuel cost modelling approved' }),
      makeScenarioAudit('scenario-fuel-escalation', '4', 'Linda Kowalski', 'committed', daysAgo(12), { fromStatus: 'active', toStatus: 'committed', detail: 'Committed as operational assumption for H1 fleet management' }),
    ],
  },

  // 21. Retention Release — Q3 Projects - Active
  {
    id: 'scenario-retention-release',
    name: 'Retention Release — Q3 Completed Projects',
    description: 'Three projects completed in Q3 2025 are now through their 6-month defects liability period. Retention monies totalling £84K are due for release from main contractors: Meridian Phase 1 (£38K), LCC Framework 2025 (£29K), and Barwell Primary School (£17K).',
    status: 'active',
    priority: 'medium',
    label: null,
    category: 'growth',
    createdBy: '6',
    createdByName: 'Sarah Mitchell',
    createdAt: daysFromNow(-11),
    modifiedAt: daysFromNow(-8),
    modifiedBy: 'Sarah Mitchell',
    shares: [
      {
        id: 'share-ret-1',
        userId: '4',
        userName: 'Linda Kowalski',
        permission: 'view',
        sharedAt: daysFromNow(-11),
        sharedBy: 'Sarah Mitchell',
      },
    ],
    isFavorite: true,
    changes: [
      {
        id: 'change-ret-1',
        type: 'win-customer',
        name: 'Meridian Phase 1 — Retention Release',
        description: 'Defects liability period expired. Claim submitted to Meridian Asset Management.',
        amount: 38000,
        date: daysFromNow(22),
        effectiveDate: daysFromNow(22),
        details: {
          customer: 'Meridian Asset Management',
          project: 'Meridian Retail Park Phase 1',
          defectsExpiry: daysFromNow(15),
          retentionRate: '5%',
          contractValue: 760000,
        },
        createdAt: daysFromNow(-11),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-ret-2',
        type: 'win-customer',
        name: 'LCC Framework 2025 — Retention Release',
        description: 'Annual framework — retention released on satisfactory completion certificate.',
        amount: 29000,
        date: daysFromNow(38),
        effectiveDate: daysFromNow(38),
        details: {
          customer: 'Leicester City Council',
          project: 'Highways Framework 2025',
          retentionRate: '3%',
          contractValue: 966667,
        },
        createdAt: daysFromNow(-11),
        createdBy: 'Sarah Mitchell',
      },
      {
        id: 'change-ret-3',
        type: 'win-customer',
        name: 'Barwell Primary School — Retention Release',
        description: 'School extension — defects period complete, final account agreed.',
        amount: 17000,
        date: daysFromNow(55),
        effectiveDate: daysFromNow(55),
        details: {
          customer: 'Leicestershire County Council',
          project: 'Barwell Primary School Extension',
          retentionRate: '5%',
          contractValue: 340000,
        },
        createdAt: daysFromNow(-11),
        createdBy: 'Sarah Mitchell',
      },
    ],
    dependencies: [],
    approvalChain: [
      {
        id: 'approval-ret-1',
        userId: '4',
        approverId: '4',
        userName: 'Linda Kowalski',
        userRole: 'Chief Financial Officer',
        status: 'approved',
        comments: 'These retentions are contractually due — good cash flow. Make sure defects certificates are formally obtained.',
        timestamp: daysFromNow(-8),
        order: 1,
      },
      {
        id: 'approval-ret-2',
        userId: '1',
        approverId: '1',
        userName: 'Catherine Hughes',
        userRole: 'Chief Executive Officer',
        status: 'approved',
        comments: 'Approved. This is money we\'ve earned — chase it promptly.',
        timestamp: daysFromNow(-7),
        order: 2,
      },
    ],
    comments: [
      {
        id: 'comment-ret-1',
        userId: '6',
        authorName: 'Sarah Mitchell',
        userRole: 'Finance Manager',
        text: 'Defects certificates obtained for Meridian and LCC. Barwell certificate expected this week. Claims submitted — expecting payment within standard 30-day terms.',
        createdAt: daysFromNow(-5),
      },
    ],
    commentCount: 1,
    versions: [
      {
        id: 'v1-ret',
        version: 1,
        description: 'Retention release schedule — Q3 completed projects',
        createdBy: 'Sarah Mitchell',
        createdByName: 'Sarah Mitchell',
        createdAt: daysFromNow(-11),
        data: {},
      },
    ],
    currentVersion: 1,
    collectionIds: [],
    metrics: {
      minCash: 247000,
      minCashDate: daysFromNow(1),
      runway: 28,
      revenueChange: 5,
      revenueChangeAmount: 84000,
      costChange: 0,
      costChangeAmount: 0,
      netImpact: 84000,
      endingCash: 331000,
    },
    tags: ['Retention', 'Cash Recovery', 'Completed Projects'],
    auditLog: [
      makeScenarioAudit('scenario-retention-release', '6', 'Sarah Mitchell', 'created', daysAgo(11), { toStatus: 'draft' }),
      makeScenarioAudit('scenario-retention-release', '6', 'Sarah Mitchell', 'submitted-for-approval', daysAgo(9), { fromStatus: 'draft', toStatus: 'pending-approval', stageOrder: 1, stageName: 'Finance Review' }),
      makeScenarioAudit('scenario-retention-release', '4', 'Linda Kowalski', 'approval-granted', daysAgo(8), { fromStatus: 'pending-approval', toStatus: 'active', stageOrder: 1, stageName: 'Finance Review', detail: 'Retention release schedule confirmed — approved' }),
    ],
  },
];

export const SCENARIO_COLLECTIONS: ScenarioCollection[] = [
  {
    id: 'collection-board',
    name: 'Board Meeting - Jan 29, 2026',
    description: 'Growth strategy scenarios for board presentation',
    scenarioIds: ['scenario-conservative', 'scenario-moderate', 'scenario-aggressive'],
    createdBy: 'Sarah Mitchell',
    createdAt: daysFromNow(-7),
    order: 1,
  },
];

/**
 * Generate engine-powered scenario forecast data for chart overlays.
 * Uses the real scenarioEngine (applyScenarioToForecast) against the base
 * forecast — replacing the old naive manual calculation.
 *
 * Returns day-by-day { date, balance } for 0-90 days.
 * Used by ForecastTimeline (90-day chart).
 */
export const generateScenarioForecastData = (scenarioId: string): { date: Date; balance: number }[] => {
  const scenario = COMPREHENSIVE_SCENARIOS.find(s => s.id === scenarioId);
  if (!scenario || scenario.changes.length === 0) {
    // No changes — return base forecast unchanged
    return generateForecastData('base').map(p => ({ date: p.date, balance: p.balance }));
  }

  const baseForecast = generateForecastData('base');
  const result = applyScenarioToForecast(baseForecast, scenario.changes);

  return result.projectedForecast.map(p => ({
    date: p.date,
    balance: p.scenarioBalance,
  }));
};

/**
 * Generate monthly scenario balances for the 12-month chart overlay.
 * Aggregates the 90-day engine output to monthly, then extends with
 * a simple net-impact projection for months 4-12.
 */
export const generateScenario12MonthData = (scenarioId: string): { balance: number }[] => {
  const scenario = COMPREHENSIVE_SCENARIOS.find(s => s.id === scenarioId);
  if (!scenario) return Array(12).fill({ balance: 0 });

  const baseForecast = generateForecastData('base');

  if (scenario.changes.length === 0) {
    // No changes — return base monthly values
    return Array.from({ length: 12 }, (_, i) => {
      const dayIndex = Math.min((i + 1) * 30, baseForecast.length - 1);
      return { balance: baseForecast[dayIndex]?.balance ?? 0 };
    });
  }

  const result = applyScenarioToForecast(baseForecast, scenario.changes);
  const projected = result.projectedForecast;

  // For the first 3 months — use engine output sampled monthly
  // For months 4-12 — apply the net monthly delta from the engine to the base
  const netMonthlyDelta = result.metrics.netImpact / 12;

  return Array.from({ length: 12 }, (_, i) => {
    if (i < 3) {
      const dayIndex = Math.min((i + 1) * 30, projected.length - 1);
      return { balance: projected[dayIndex]?.scenarioBalance ?? 0 };
    }
    // Beyond 90-day window — base balance + scaled net impact
    const baseMonthDayIndex = Math.min((i + 1) * 30, baseForecast.length - 1);
    const baseBalance = baseForecast[baseMonthDayIndex]?.balance ?? 0;
    return { balance: Math.round(baseBalance + netMonthlyDelta * (i + 1)) };
  });
};

/**
 * Generate quarterly scenario balances for the 3-year chart overlay.
 * Uses net impact projection scaled across 12 quarters.
 */
export const generateScenario3YearData = (scenarioId: string): { balance: number }[] => {
  const scenario = COMPREHENSIVE_SCENARIOS.find(s => s.id === scenarioId);
  if (!scenario) return Array(12).fill({ balance: 0 });

  const baseForecast = generateForecastData('base');

  if (scenario.changes.length === 0) {
    return Array.from({ length: 12 }, (_, i) => {
      const dayIndex = Math.min((i + 1) * 91, baseForecast.length - 1);
      return { balance: baseForecast[dayIndex]?.balance ?? 0 };
    });
  }

  const result = applyScenarioToForecast(baseForecast, scenario.changes);
  const netQuarterlyDelta = result.metrics.netImpact / 12;

  // 3-year base values (quarterly) — derived from the base 90-day endpoint, then projected
  const base3YearValues = [247000, 69000, 135000, 98000, 172000, 155000, 195000, 178000, 225000, 208000, 252000, 235000];

  return base3YearValues.map((baseVal, i) => ({
    balance: Math.round(baseVal + netQuarterlyDelta * (i + 1)),
  }));
};
